import React from 'react'
import { m } from 'framer-motion'
import { Star, ExternalLink } from 'lucide-react'
import { useTheme } from '../../../context/ThemeContext'
import { ReviewsContent } from '../../../types/content.types'

interface ReviewsProps {
    content: ReviewsContent
}

const Reviews: React.FC<ReviewsProps> = ({ content }) => {
    const { theme } = useTheme()
    const reviews = content.items

    return (
        <section
            id="reviews"
            className={`restaurant-reviews theme-${theme.mode}`}
        >
            <div className="restaurant-reviews-container">
                {/* 섹션 헤더 */}
                <m.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="restaurant-reviews-header"
                >
                    <span className="restaurant-reviews-label">{content.label}</span>
                    <div className="h-px w-12 bg-gray-600 mx-auto mt-4 mb-6" />
                    <h2 className="restaurant-reviews-title">{content.title}</h2>
                    <p className="restaurant-reviews-description">
                        {content.description}
                    </p>
                </m.div>

                {/* 리뷰 그리드 */}
                <div className="restaurant-reviews-grid">
                    {reviews.map((review, index) => (
                        <m.div
                            key={review.id}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            className="restaurant-review-card"
                        >
                            {/* 좌측 상단 장식 라인 */}
                            <div className="restaurant-review-decoration restaurant-review-decoration-top-left" />
                            <div className="restaurant-review-decoration restaurant-review-decoration-top-left-vertical" />

                            {/* 별점 */}
                            <div className="restaurant-review-rating">
                                {[...Array(review.rating)].map((_, i) => (
                                    <Star
                                        key={i}
                                        size={16}
                                        className="restaurant-review-star"
                                    />
                                ))}
                            </div>

                            {/* 리뷰 내용 */}
                            <p className="restaurant-review-comment">
                                "{review.comment}"
                            </p>

                            {/* 하단 정보 */}
                            <div className="restaurant-review-footer">
                                <div>
                                    <p className="restaurant-review-name">
                                        {review.name}
                                    </p>
                                </div>
                                <p className="restaurant-review-date">
                                    {review.date}
                                </p>
                            </div>

                            {/* 우측 하단 장식 라인 */}
                            <div className="restaurant-review-decoration restaurant-review-decoration-bottom-right" />
                            <div className="restaurant-review-decoration restaurant-review-decoration-bottom-right-vertical" />
                        </m.div>
                    ))}
                </div>

                {/* 네이버 플레이스 링크 */}
                <m.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="restaurant-reviews-link"
                >
                    <a
                        href={content.moreReviewsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="restaurant-reviews-external-link"
                    >
                        <ExternalLink size={18} />
                        <span>{content.moreReviews}</span>
                    </a>
                </m.div>
            </div>
        </section>
    )
}

export default React.memo(Reviews)
